import { ArrowRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { CallsChart } from "@/components/calls-chart";
import { Card, CardContent } from "@/components/ui/card";
import type { McpUsage } from "@/lib/api";
import { formatNumber, formatShare } from "@/lib/format";
import { lastDaysLabel } from "@/lib/usage-window";
import { cn } from "@/lib/utils";

interface RecentUseProps {
  /** The window in days: today and the days before it, Belgian time (not the calendar week). */
  days: number;
  /** The admin usage of that window; undefined while it loads or when it failed. */
  usage: McpUsage | undefined;
  isLoading: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * For admins: the connector's last `days` days at a glance (calls, active
 * users and errors with their share of the calls, and the calls per day split
 * into successful and failed),
 * with the way to the Analytics page. A rolling window, so it is titled
 * "Afgelopen 7 dagen", never "Deze week".
 */
export function RecentUse({ days, usage, isLoading, onRetry, className }: RecentUseProps) {
  const { t } = useTranslation();
  const period = lastDaysLabel(days);
  return (
    <Card className={cn("flex flex-col", className)}>
      <CardContent className="flex flex-1 flex-col gap-5 pt-6 max-sm:px-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-heading text-primary text-lg font-bold">{period}</h2>
          <Link
            to="/analytics"
            className="text-accent tap-target inline-flex items-center gap-1 text-sm font-medium hover:underline pointer-coarse:relative"
          >
            {t("nav.analytics")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <Loader2 className="text-muted h-6 w-6 animate-spin" aria-label={t("common.loading")} />
          </div>
        ) : usage ? (
          <UsageNumbers usage={usage} />
        ) : (
          <p className="text-muted py-8 text-center text-sm">
            {t("home.usageLoadError")}{" "}
            <button type="button" onClick={onRetry} className="underline">
              {t("common.tryAgain")}
            </button>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function UsageNumbers({ usage }: { usage: McpUsage }) {
  const { t } = useTranslation();
  const { totals } = usage;
  const stats = [
    { key: "calls", label: t("usage.calls"), value: totals.calls, note: null },
    { key: "users", label: t("usage.activeUsers"), value: totals.active_users, note: null },
    {
      key: "errors",
      label: t("usage.errors"),
      value: totals.errors,
      // Every failure counts: always with its share of the calls, as on Statistieken.
      note:
        totals.calls > 0
          ? t("analytics.shareOfCalls", { share: formatShare(totals.errors / totals.calls) })
          : null,
    },
  ];

  return (
    <>
      {/* On a phone the boxes come closer and a long label hyphenates, so three still fit. */}
      <dl className="grid grid-cols-3 gap-2 sm:gap-3">
        {stats.map((stat) => (
          <div key={stat.key} className="border-border rounded-md border px-2 py-2 sm:px-3">
            <dt className="text-muted text-xs hyphens-auto">{stat.label}</dt>
            <dd className="text-primary text-xl font-bold tabular-nums sm:text-2xl">
              {formatNumber(stat.value)}
            </dd>
            {stat.note && (
              <dd className="text-muted text-xs hyphens-auto tabular-nums">{stat.note}</dd>
            )}
          </div>
        ))}
      </dl>
      {totals.calls === 0 ? (
        <p className="text-muted py-6 text-center text-sm">
          {t("usage.noCalls", { count: usage.days })}
        </p>
      ) : (
        <CallsChart data={usage.per_day} compact />
      )}
    </>
  );
}
